// Small fetch wrapper and shared hooks.

import { useCallback, useEffect, useRef, useState } from "react";

export class ApiError extends Error {
  constructor(message: string, public status: number, public data: any) {
    super(message);
  }
}

export async function api<T = any>(path: string, opts: { method?: string; body?: unknown; raw?: BodyInit; type?: string } = {}): Promise<T> {
  const headers: Record<string, string> = {};
  let body: BodyInit | undefined;
  if (opts.raw !== undefined) {
    headers["content-type"] = opts.type ?? "application/octet-stream";
    body = opts.raw;
  } else if (opts.body !== undefined) {
    headers["content-type"] = "application/json";
    body = JSON.stringify(opts.body);
  }
  const r = await fetch(path, { method: opts.method ?? (body ? "POST" : "GET"), headers, body, credentials: "same-origin" });
  const text = await r.text();
  let data: any = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (r.status === 401 && path !== "/api/login") window.dispatchEvent(new Event("rc-logout"));
  if (!r.ok) throw new ApiError(data?.error ?? `Fehler ${r.status}`, r.status, data);
  return data as T;
}

/** Load once (and on reload()). */
export function useLoad<T = any>(path: string | null, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const reload = useCallback(async () => {
    if (!path) return;
    setLoading(true);
    try {
      setData(await api<T>(path));
      setError(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [path]);
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload, ...deps]);
  return { data, error, loading, reload, setData };
}

/** Poll every `ms`; pauses while the tab is in the background. */
export function usePoll<T = any>(path: string, ms: number) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      if (document.visibilityState === "visible") {
        try {
          const d = await api<T>(path);
          if (alive) {
            setData(d);
            setError(null);
          }
        } catch (e: any) {
          if (alive) setError(e.message);
        }
      }
      if (alive) timer.current = window.setTimeout(tick, ms);
    };
    tick();
    const onVis = () => {
      if (document.visibilityState === "visible") {
        window.clearTimeout(timer.current);
        tick();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      alive = false;
      window.clearTimeout(timer.current);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [path, ms]);
  return { data, error };
}

// ---- toast

export function toast(text: string, bad = false) {
  window.dispatchEvent(new CustomEvent("rc-toast", { detail: { text, bad } }));
}

// ---- formatting

export function fmtMs(ms: number | null | undefined): string {
  if (ms == null || !isFinite(ms)) return "–";
  const s = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = String(s % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${r}` : `${m}:${r}`;
}

export function fmtTime(iso: string | null | undefined): string {
  if (!iso) return "–";
  const d = new Date(iso);
  const today = new Date().toDateString() === d.toDateString();
  return today
    ? d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" })
    : d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export function since(iso: string | null | undefined): string {
  if (!iso) return "";
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `seit ${s} s`;
  if (s < 3600) return `seit ${Math.floor(s / 60)} min`;
  if (s < 86400) return `seit ${Math.floor(s / 3600)} h ${Math.floor((s % 3600) / 60)} min`;
  return `seit ${Math.floor(s / 86400)} Tagen`;
}
